import * as React from 'react';
import { useParams } from 'react-router';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { logisticsDetailedSKUURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { LOGISTICS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { PalletHeightUnitEnum, useLogisticsSkuForEditQuery } from '@admin/schema';

import { EditFormData, LogisticsSkuForm } from './form/form';

export const LogisticsEditSku: React.FC = () => {
  useTitle('Edit SKU');
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useLogisticsSkuForEditQuery({ client, variables: { id: id } });

  if (loading || !data) {
    return <Spinner />;
  }

  const sku = data.logisticsSku;
  const defaultPalletHeight =
    sku.defaultPalletHeight?.unit === PalletHeightUnitEnum.Inches ? sku.defaultPalletHeight.value : 0;
  const formData: EditFormData = {
    id: sku.id,
    value: sku.value,
    description: sku.description || '',
    hasBarcode: sku.hasBarcode,
    defaultPalletTypeID: sku.defaultPalletTypeId || '0',
    defaultPalletHeightValue: defaultPalletHeight,
    itemsPerPallet: sku.itemsPerPallet || undefined,
    weightPerPallet: sku.weightPerPallet || undefined,
    gtinBarcode: sku.gtinBarcode || undefined,
    nmfcClass: sku.nmfcClass || undefined,
    nmfcNumber: sku.nmfcNumber || undefined,
    itemWeight: sku.itemWeight || undefined,
    itemLength: sku.itemLength || undefined,
    itemWidth: sku.itemWidth || undefined,
    itemHeight: sku.itemHeight || undefined,
    teachInStatus: sku.teachInStatus,
    disableVBW: !sku.vbwEligible,
    vbwOptOutReason: sku.vbwOptOutReason || undefined,
    countingUnit: sku.countingUnit,
  };

  const onSubmit = async () => {
    window.location.href = logisticsDetailedSKUURL({ id: sku.id });
  };

  return (
    <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update}>
      <LogisticsSkuForm formData={formData} onSave={onSubmit} />
    </Permission>
  );
};
